import { useState } from 'react';

import { ApiError, api } from '../../api';
import { go } from '../../App';
import { useLoad, usePolled } from '../../data';
import { ConnectionState, Loading, PageHead, Panel } from '../../ui';
import { Connection, Onboarding } from '../../types';
import { ANOTHER_HOTEL, END_HOTEL, athensDay, standingChip, standingLine } from '../../joining';
import { AnswerButton, rowFocusId, useAnswers, type AnswerKind } from './answers';

// My hotels (canon 09 supplier "My hotels" · Register; §4.2 "Hotels"). The
// supplier's own side of the connection graph — the hotels that can book them,
// and the ones still asking. Over the SAME GET /v1/connections the Today card
// reads; a pending row accepts inline through the SAME
// POST /v1/connections/{hotelAccountId}/accept, so there is one accept path, not
// two. No new endpoint.
//
// B33 — nothing goes live before Finish. While the supplier is still setting up
// (GET /v1/onboarding, the read Today already makes) the API refuses an accept,
// so a pending row offers none: it says the hotel is waiting for them and the
// row opens the setup checklist on Today. The hotel that invited them goes live
// at Finish; one that asked another way shows Accept here once they have
// finished. The table waits for both reads, so an Accept never flashes up first.
//
// B35 — each live hotel is labelled by whether it can book you now, the API's
// standing (a listed trip, open for work, today in season): "Can book you" only
// then, otherwise who has the next step. Until 2026-10-21 every live hotel read
// "Connected" under a page that said "The hotels that can book you".

export function Hotels() {
  const { data, error, reload } = usePolled<{ items: Connection[] }>('/connections');
  const onboarding = useLoad<Onboarding>('/onboarding');
  // If /onboarding fails outright the rows fall back to Accept. The server's
  // refusal is said under the row and re-reads setup, so the row turns into the
  // waiting state with its way forward instead of staying an Accept.
  const setupKnown = onboarding.data !== null || !!onboarding.error;
  const settingUp = onboarding.data?.status === 'onboarding';
  const items = data && setupKnown ? data.items : null;
  // N42 (DECISION-080 §6): Not now on a request, End on a live hotel — one tap,
  // with the design system's undo toast over the 30-second window (N42 fix
  // round). A hotel answered here leaves the table while its window is open.
  const { answer, isHidden, toast } = useAnswers(reload, data?.items);
  // The hotels they no longer work with, kept below with when (canon 13): ended,
  // and since the fix round said not now to — with an Accept, their way back
  // (George). An End stays final until the hotel asks again.
  const past = (items ?? []).filter((c) => (c.status === 'ended' || c.status === 'declined') && !isHidden(c));
  const rows = (items ?? [])
    .filter((c) => (c.status === 'pending' || c.status === 'active') && !isHidden(c))
    .slice()
    // Pending (needs the supplier) first, then active, name-stable within each.
    .sort((a, b) =>
      Number(a.status === 'active') - Number(b.status === 'active') ||
      (a.name ?? '').localeCompare(b.name ?? ''),
    );
  const active = rows.filter((c) => c.status === 'active').length;

  return (
    <div>
      <PageHead title="My hotels" sub="Your hotels, whether each can book you now, and the ones asking to work with you." />

      {error && data === null && <p className="error-line">{error}</p>}
      {toast}
      {items === null ? (
        <Loading />
      ) : rows.length === 0 && past.length === 0 ? (
        <div className="empty">
          <strong>No hotels yet</strong>
          {/* B33: not "Accepting lets them book you directly". Nothing can be
              accepted during setup, and the hotel that invited you is connected
              by Finish, never by an Accept. Same words as the phone. */}
          <p>A hotel that invites you or asks to work with you appears here and on Today.</p>
        </div>
      ) : (
        <Panel style={{ overflow: 'hidden' }} flush meta={`${active} connected`}>
          <table>
            <thead>
              <tr>
                <th>Hotel</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <HotelRow
                  key={c.hotelAccountId ?? c.accountId}
                  c={c}
                  settingUp={settingUp}
                  onAccepted={reload}
                  onAnswer={answer}
                  onSetupNotFinished={() => void onboarding.reload()}
                />
              ))}
              {/* N42: the hotels they no longer work with — the history is kept. */}
              {past.map((c) => (
                <PastRow
                  key={`past-${c.hotelAccountId ?? c.accountId}`}
                  c={c}
                  settingUp={settingUp}
                  onAccepted={reload}
                  onSetupNotFinished={() => void onboarding.reload()}
                />
              ))}
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
}

/**
 * A hotel they no longer work with: ended (final until the hotel asks again), or
 * said not now to — "You said not now on {day}. They can ask again." — with an
 * Accept, the supplier's way back (George, N42 fix round), under the same B33
 * rule as any Accept: during setup the server's refusal is said here.
 */
function PastRow({
  c,
  settingUp,
  onAccepted,
  onSetupNotFinished,
}: {
  c: Connection;
  settingUp: boolean;
  onAccepted: () => Promise<void>;
  onSetupNotFinished: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const hotelId = c.hotelAccountId ?? c.accountId ?? '';
  const name = c.name ?? c.hotelName ?? hotelId;
  const notNow = c.status === 'declined';
  const accept = async () => {
    setBusy(true);
    setError('');
    try {
      await api('POST', `/connections/${hotelId}/accept`);
      await onAccepted();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
      if (e instanceof ApiError && e.code === 'setup_not_finished') onSetupNotFinished();
    }
  };
  return (
    <tr>
      <td id={rowFocusId(c)} tabIndex={-1}>
        {name}
        {c.answeredAt && (
          <>
            <br />
            <span className="hint">{notNow ? ANOTHER_HOTEL.historyLine(athensDay(c.answeredAt)) : END_HOTEL.historyLine(athensDay(c.answeredAt))}</span>
          </>
        )}
      </td>
      <td>
        <ConnectionState kind="dormant" label={notNow ? ANOTHER_HOTEL.chip : END_HOTEL.chip} />
        {error && <p className="error-line" role="alert" style={{ marginTop: 'var(--space-xs)' }}>{error}</p>}
      </td>
      <td className="num" style={{ whiteSpace: 'nowrap' }}>
        {notNow && !settingUp && (
          <button className="btn secondary" aria-label={ANOTHER_HOTEL.acceptLabel(name)} disabled={busy} onClick={accept}>
            {busy ? ANOTHER_HOTEL.accepting : ANOTHER_HOTEL.accept}
          </button>
        )}
      </td>
    </tr>
  );
}

function HotelRow({
  c,
  settingUp,
  onAccepted,
  onAnswer,
  onSetupNotFinished,
}: {
  c: Connection;
  settingUp: boolean;
  onAccepted: () => Promise<void>;
  onAnswer: (kind: AnswerKind, c: Connection) => Promise<void>;
  // The server said setup isn't finished (B33): re-read it, so this row shows
  // the waiting state and its way to the checklist.
  onSetupNotFinished: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const hotelId = c.hotelAccountId ?? c.accountId ?? '';
  const name = c.name ?? c.hotelName ?? hotelId;
  // A hotel waiting on this supplier's setup — not an Accept (B33).
  const waitingOnSetup = settingUp && c.status !== 'active';
  // N42: a hotel that asked (or invited the number) — Not now beside Accept,
  // during setup too; never the hotel their own yes on its link connected.
  const asked = c.status === 'pending' && (c.via === 'request' || c.via === 'invited');
  const live = c.status === 'active' ? standingChip(c) : null;

  const accept = async () => {
    setBusy(true);
    setError('');
    try {
      await api('POST', `/connections/${hotelId}/accept`);
      await onAccepted();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
      if (e instanceof ApiError && e.code === 'setup_not_finished') onSetupNotFinished();
    }
  };

  return (
    <tr className={waitingOnSetup ? 'click' : ''} onClick={waitingOnSetup ? () => go('/today') : undefined}>
      <td id={rowFocusId(c)} tabIndex={-1}>
        {name}
        {waitingOnSetup && (
          <>
            <br />
            {/* Only the supplier's own step is promised: the invitation's hotel
                still lists a trip before it can book, and any other still needs
                an Accept. */}
            <span className="hint">{asked ? ANOTHER_HOTEL.waitsForFinish(name) : `Finish setting up on Today — ${name} is waiting for you.`}</span>
          </>
        )}
        {!waitingOnSetup && c.status === 'pending' && c.via && (
          <>
            <br />
            <span className="hint">
              {c.via === 'invited' ? `${ANOTHER_HOTEL.invitedToo(name, c.invitedBy)} ` : ''}
              {ANOTHER_HOTEL.sees(name)}
            </span>
          </>
        )}
        {live && (
          <>
            <br />
            <span className="hint">{standingLine(c)}</span>
            <br />
            <span className="hint">{END_HOTEL.before(name)}</span>
          </>
        )}
      </td>
      <td>
        <ConnectionState
          kind={live ? live.kind : 'pending'}
          label={live ? live.label : waitingOnSetup ? 'Waiting for you to finish' : c.via === 'invited' ? ANOTHER_HOTEL.alsoInvited : ANOTHER_HOTEL.wants}
        />
        {error && <p className="error-line" role="alert" style={{ marginTop: 'var(--space-xs)' }}>{error}</p>}
      </td>
      <td className="num" style={{ whiteSpace: 'nowrap' }}>
        {waitingOnSetup ? (
          asked ? <AnswerButton kind="not-now" c={c} onAnswer={onAnswer} /> : <span className="open">Finish setting up →</span>
        ) : c.status !== 'active' ? (
          <>
            <button className="btn secondary" aria-label={ANOTHER_HOTEL.acceptLabel(name)} disabled={busy} onClick={accept}>
              {busy ? ANOTHER_HOTEL.accepting : ANOTHER_HOTEL.accept}
            </button>
            {asked && <AnswerButton kind="not-now" c={c} onAnswer={onAnswer} />}
          </>
        ) : (
          <AnswerButton kind="end" c={c} onAnswer={onAnswer} />
        )}
      </td>
    </tr>
  );
}
